import React, { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn, type SvgIcon, Icon } from '@bridebook/aura';
import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '@bridebook/aura/src/lib/type-helpers';

const tagVariants = cva(
  [
    'inline-flex w-fit items-center justify-center rounded text-left',
    'font-sans leading-4 font-normal select-none',
    'transition-all duration-150',
  ],
  {
    variants: {
      theme: {
        navyGrowth: 'border-navy-growth bg-navy-growth text-indigo-crush-10',
        indigoCrush10: 'border-indigo-crush-10 bg-indigo-crush-10 text-space',
        indigoCrush20: 'border-indigo-crush-20 bg-indigo-crush-20 text-space',
        indigoCrush: 'border-indigo-crush bg-indigo-crush text-white',
        peachRose120: 'border-peach-rose-120 bg-peach-rose-120 text-space',
        peachRose60: 'border-peach-rose-60 bg-peach-rose-60 text-space',
        pinkJoy120: 'border-peach-rose-60 bg-pink-joy-120 text-space',
        teal80: 'border-teal-80 bg-teal-80 text-space',
        teal20: 'border-teal-20 bg-teal-20 text-space',
        blushTangerine60: 'border-blush-tangerine-60 bg-blush-tangerine-60 text-space',
        blushTangerine: 'border-blush-tangerine bg-blush-tangerine text-space',
        white: 'border-white bg-white text-space',
        space08: 'border-space-08 bg-space-08 text-space',
        transparent: 'border-transparent bg-transparent text-space',
        highlight: 'border-highlights bg-highlights text-space',
      },
      size: {
        small: 'h-4 gap-1 px-1.5 text-[10px]',
        normal: 'h-6 gap-1 px-2 text-xs',
      },
      bordered: {
        true: 'border bg-white text-space',
      },
      interactive: {
        true: 'cursor-pointer hover:opacity-80 focus-visible:ring-[3px] focus-visible:ring-focus-ring focus-visible:ring-offset-2 focus-visible:outline-none',
      },
    },
  },
);

export type TagThemes = VariantProps<typeof tagVariants>['theme'];

// Helper type to remove null from variant props
type NonNullableVariantProps<T> = {
  [K in keyof T]: NonNullable<T[K]>;
};

// Extract the variant props type from CVA without null
type TagVariantProps = NonNullableVariantProps<VariantProps<typeof tagVariants>>;

type TagProps = {
  /**
   * Choose from pre-defined themes for ease of use
   */
  theme?: TagThemes;
  /**
   * Choose size variant for different paddings and font sizes
   */
  size?: TagVariantProps['size'];
  /**
   * Adds only a border instead of a full background
   */
  bordered?: boolean;
  /**
   * Pass an icon to render on the left side of content
   */
  iconLeft?: SvgIcon;
  /**
   * Pass an icon to render on the right side of content
   */
  iconRight?: SvgIcon;
  /**
   * Custom class names to apply to icon containers
   */
  classNames?: {
    iconLeft?: string;
    iconRight?: string;
  };
};

const defaultComponent = 'div';

interface PolymorphicTag {
  <C extends React.ElementType = typeof defaultComponent>(
    props: PolymorphicComponentPropsWithRef<C, TagProps>,
  ): React.ReactNode;
}

const TagComponent = <C extends React.ElementType = typeof defaultComponent>(
  {
    as,
    children,
    theme = 'space08',
    size = 'normal',
    bordered = false,
    iconLeft,
    iconRight,
    className,
    classNames,
    ...props
  }: PolymorphicComponentPropsWithRef<C, TagProps>,
  ref: PolymorphicRef<C>,
) => {
  const Component = as || defaultComponent;
  const iconSize = size === 'small' ? 12 : 16;

  // Auto-detect interactive elements
  const interactive =
    Component === 'button' || Component === 'a' || 'onClick' in props || 'href' in props;

  return (
    <Component
      ref={ref}
      className={cn(
        tagVariants({
          theme,
          size,
          bordered,
          interactive,
        }),
        className,
      )}
      {...props}>
      {iconLeft && <Icon icon={iconLeft} size={iconSize} className={classNames?.iconLeft} />}
      <span className={cn('flex-1 overflow-hidden text-center text-ellipsis whitespace-nowrap')}>
        {children}
      </span>
      {iconRight && <Icon icon={iconRight} size={iconSize} className={classNames?.iconRight} />}
    </Component>
  );
};

export const Tag: PolymorphicTag = forwardRef(TagComponent);
